import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Avatar,
  Typography,
  MenuItem,
  TextField,
  Button,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import {
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { DefaultPage } from "../../../layouts/default-page";
import { DataTableFive } from "../../../layouts/layout2/data-table-five";
import { StatusChip } from "../../../components/common/StatusChip";
import { RowActions } from "../../../components/common/RowActions";
import { useGetCinemasQuery, useLazyGetCinemasQuery } from "../../../services/cinema.query";
import { ExportLoadingBackdrop } from "../../../components/common/ExportLoadingBackdrop";
import { useTableState } from "../../../hooks/useTableState";
import { Notify } from "../../../layouts/notify";

export default function TheatresPage() {
  const navigate = useNavigate();

  const defaultFilters = useMemo(
    () => ({
      mallFilter: "without" as "with" | "without" | "all",
      searchTerm: "",
    }),
    []
  );

  const {
    page,
    pageSize,
    filters,
    hasActiveFilters,
    setPage,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
  });

  const [draftMallFilter, setDraftMallFilter] = useState(filters.mallFilter);
  const [draftSearch, setDraftSearch] = useState(filters.searchTerm);

  useEffect(() => {
    setDraftMallFilter(filters.mallFilter);
    setDraftSearch(filters.searchTerm);
  }, [filters]);

  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const [triggerFetchAllCinemas] = useLazyGetCinemasQuery();

  const start = (page - 1) * pageSize;
  const end = start + pageSize - 1;

  const { data, isLoading, isFetching, error } = useGetCinemasQuery(
    {
      start,
      end,
      hasMall: filters.mallFilter === "all" ? undefined : filters.mallFilter,
      searchTerm: filters.searchTerm,
    },
    { refetchOnMountOrArgChange: true }
  );

  const theatres = data?.list ?? [];
  const totalRecords = data?.count ?? 0;
  const currentYear = new Date().getFullYear();

  const handleSearch = () => {
    setFilters({
      mallFilter: draftMallFilter,
      searchTerm: draftSearch.trim(),
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching theatres...");
    try {
      const result = await triggerFetchAllCinemas({
        start: 0,
        end: 999999,
        hasMall: filters.mallFilter === "all" ? undefined : filters.mallFilter,
        searchTerm: filters.searchTerm,
      }).unwrap();

      const allRows = result?.list || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Preparing PDF document for all ${allRows.length.toLocaleString()} theatres...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const doc = new jsPDF("landscape", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Registered Theatres & Cinemas Directory", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Filter: Mall = ${filters.mallFilter} | Total Theatres: ${allRows.length}`,
        14,
        22
      );

      const tableColumn = [
        "#",
        "Theatre Name",
        "Linked Mall / Group",
        "Type",
        "Status",
        "Permit Validity",
      ];

      const tableRows = allRows.map((item: any, idx: number) => {
        const permitYear = item.last_registration_year ? Number(item.last_registration_year) : null;
        const isExpired = permitYear ? permitYear < currentYear : false;
        const permitStatus = !permitYear ? "Unregistered" : isExpired ? `Expired (${permitYear})` : `Valid (${permitYear})`;

        return [
          idx + 1,
          item.name || "—",
          item.theatre_group?.name || "Independent",
          item.type || "REGULAR",
          item.is_active ? "ACTIVE" : "INACTIVE",
          permitStatus,
        ];
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Theatres_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${allRows.length} theatres to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching theatres...");
    try {
      const result = await triggerFetchAllCinemas({
        start: 0,
        end: 999999,
        hasMall: filters.mallFilter === "all" ? undefined : filters.mallFilter,
        searchTerm: filters.searchTerm,
      }).unwrap();

      const allRows = result?.list || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Generating Excel spreadsheet for all ${allRows.length.toLocaleString()} theatres...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const rows = allRows.map((item: any, idx: number) => {
        const permitYear = item.last_registration_year ? Number(item.last_registration_year) : null;
        const isExpired = permitYear ? permitYear < currentYear : false;
        const permitStatus = !permitYear ? "Unregistered" : isExpired ? `Expired (${permitYear})` : `Valid (${permitYear})`;

        return {
          "No.": idx + 1,
          "Theatre Name": item.name || "—",
          "Linked Mall / Group": item.theatre_group?.name || "Independent",
          "Type": item.type || "REGULAR",
          "Status": item.is_active ? "ACTIVE" : "INACTIVE",
          "Permit Year": item.last_registration_year || "—",
          "Permit Status": permitStatus,
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Theatres Directory");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 28 },
        { wch: 28 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
        { wch: 20 },
      ];

      XLSX.writeFile(wb, `MTRCB_Theatres_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${allRows.length} theatres to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "rowNumber",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const number = (page - 1) * pageSize + row.index + 1;
          return <Typography>{number}</Typography>;
        },
      },
      {
        accessorKey: "name",
        header: "Theatre Name",
        size: 280,
      },
      {
        id: "mall",
        header: "Mall",
        size: 260,
        Cell: ({ row }: any) => {
          const group = row.original.theatre_group;
          if (!group) return "—";

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                src={group.logo_url}
                alt={group.name}
                variant="rounded"
                sx={{ width: 35, height: 35 }}
              />
              <Typography>{group.name}</Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 140,
        Cell: ({ cell }: any) => {
          const type = (cell.getValue() || "REGULAR").toUpperCase();
          return (
            <StatusChip
              label={type}
              tone={type === "REGULAR" ? "info" : "warning"}
            />
          );
        },
      },
      {
        accessorKey: "is_active",
        header: "Status",
        size: 130,
        Cell: ({ cell }: any) => {
          const active = cell.getValue();
          return (
            <StatusChip
              label={active ? "ACTIVE" : "INACTIVE"}
              tone={active ? "success" : "neutral"}
            />
          );
        },
      },
      {
        id: "permit",
        header: "Permit",
        size: 140,
        Cell: ({ row }: any) => {
          const year = row.original.last_registration_year
            ? Number(row.original.last_registration_year)
            : null;

          if (!year) {
            return <StatusChip label="No Record" tone="neutral" />;
          }

          const isExpired = year < currentYear;

          return (
            <StatusChip
              label={isExpired ? `Expired (${year})` : `Valid (${year})`}
              tone={isExpired ? "error" : "success"}
            />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 130,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <RowActions
            actions={[
              {
                label: "View",
                onClick: () =>
                  navigate(`/movie-house/theaters/${row.original.id}`),
              },
            ]}
          />
        ),
      },
    ],
    [page, pageSize, currentYear, navigate]
  );

  return (
    <DefaultPage 
      title="Theatres" 
      subTitle="Manage and review registered theatres and cinemas."
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays registered theatres. Set the filters and click <strong>Search</strong> to load results.
        </Alert>
      </Box>

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              select
              size="small"
              label="Mall Linked"
              value={draftMallFilter}
              onChange={(e) => setDraftMallFilter(e.target.value as "with" | "without" | "all")}
              sx={{
                minWidth: 160,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            >
              <MenuItem value="all">All Theatres</MenuItem>
              <MenuItem value="with">With Mall</MenuItem>
              <MenuItem value="without">Without Mall</MenuItem>
            </TextField>

            <TextField
              size="small"
              label="Search theatre, type, or status"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              sx={{
                minWidth: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {!!error && (
        <Box mb={2}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load theatres.
          </Alert>
        </Box>
      )}

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalRecords || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={theatres}
        total={totalRecords}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: { id: any }) => String(row.id)}
        isLoading={isLoading || isFetching}
        onRowClick={(row: any) => navigate(`/movie-house/theaters/${row.original.id}`)}
      />

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />
    </DefaultPage>
  );
}